// frontend/src/components/common/Toast.jsx
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertIcon, CheckIcon, CloseIcon, InfoIcon } from "./Icon";
import "./ui.css";

const ToastContext = createContext(null);
let _id = 0;

const ICONS = {
  success: <CheckIcon size={16} />,
  error: <AlertIcon size={16} />,
  warning: <AlertIcon size={16} />,
  info: <InfoIcon size={16} />,
};

function ToastItem({ toast, onDismiss }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!toast.duration) return undefined;
    const t = setTimeout(() => setLeaving(true), toast.duration);
    return () => clearTimeout(t);
  }, [toast.duration]);

  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(() => onDismiss(toast.id), 200);
    return () => clearTimeout(t);
  }, [leaving, onDismiss, toast.id]);

  return (
    <div
      className={`toast toast--${toast.type}${leaving ? " is-leaving" : ""}`}
      role={toast.type === "error" ? "alert" : "status"}
    >
      <span className="toast__icon" aria-hidden="true">{ICONS[toast.type] || ICONS.info}</span>
      <div className="toast__body">
        {toast.title && <p className="toast__title">{toast.title}</p>}
        {toast.message && <p className="toast__msg">{toast.message}</p>}
        {toast.action && (
          <button
            type="button"
            className="toast__action"
            onClick={() => {
              toast.action.onClick?.();
              setLeaving(true);
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button type="button" className="toast__close" aria-label="Dismiss notification" onClick={() => setLeaving(true)}>
        <CloseIcon size={14} />
      </button>
    </div>
  );
}

/** Wrap the app once. Toasts stack bottom-right (bottom-center on mobile). */
export function ToastProvider({ children, max = 4 }) {
  const [toasts, setToasts] = useState([]);
  const maxRef = useRef(max);

  const dismiss = useCallback((id) => {
    setToasts((list) => (id == null ? [] : list.filter((t) => t.id !== id)));
  }, []);

  const toast = useCallback((input) => {
    const opts = typeof input === "string" ? { message: input } : input || {};
    const id = ++_id;
    const item = {
      id,
      type: opts.type || "info",
      title: opts.title,
      message: opts.message,
      action: opts.action,
      duration: opts.duration === undefined ? (opts.type === "error" ? 7000 : 4500) : opts.duration,
    };
    setToasts((list) => [...list, item].slice(-maxRef.current));
    return id;
  }, []);

  const api = useMemo(() => {
    const make = (type) => (message, opts = {}) =>
      toast({ ...(typeof message === "string" ? { message } : message), ...opts, type });
    return {
      toast,
      dismiss,
      success: make("success"),
      error: make("error"),
      info: make("info"),
      warning: make("warning"),
    };
  }, [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" aria-live="polite" aria-relevant="additions">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * const { toast, success, error, info, warning, dismiss } = useToast();
 * toast({ type, title, message, duration, action: { label, onClick } }) or toast("text")
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}

export default ToastProvider;
